import { useEffect, useRef, useState } from 'react';
import { collabApi } from '../../api/collab.js';
import { buildMentionToken } from './mentionText.js';

/**
 * BOS+ Phase 14b — messenger composer with @mention autocomplete.
 *
 * Unlike the legacy `components/MentionInput` (which inserts a fuzzy
 * `@email-prefix` the server resolves by DB lookup), this inserts the
 * unambiguous STRUCTURED token `@[Name](user:uuid)` that the messenger parser
 * expects — so a mention always resolves to exactly one user and carries its
 * display name in the body.
 *
 * It also owns Enter-to-send: while the mention dropdown is open, Enter/Arrow/
 * Esc/Tab drive the dropdown; when it's closed, Enter (no Shift) calls onSubmit
 * and Shift+Enter inserts a newline. Reuses the app's `.mention-input` styles.
 */
export default function MentionTextarea({ value, onChange, onSubmit, disabled, placeholder, rows = 2 }) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [active, setActive] = useState(0);
  const [query, setQuery] = useState('');
  const taRef = useRef(null);
  const caretRef = useRef(null); // caret to restore after a token insertion
  const seqRef = useRef(0);      // search generation — drops out-of-order responses

  function update(e) {
    const v = e.target.value;
    onChange(v);
    const caret = e.target.selectionStart;
    const before = v.slice(0, caret);
    // The @word immediately left of the caret. Stops at whitespace / token punct.
    const m = before.match(/@([\w.-]*)$/);
    if (m) {
      setQuery(m[1]);
      setOpen(true);
    } else {
      setOpen(false);
      setQuery('');
    }
  }

  // Debounced user search — only while the dropdown is open with a query. Each
  // run bumps a generation counter; a slower earlier response whose generation
  // is stale is ignored, so the menu always reflects the latest query (and a
  // late response after unmount is dropped).
  useEffect(() => {
    if (!open || !query) { setResults([]); return; }
    const mySeq = ++seqRef.current;
    const t = setTimeout(() => {
      collabApi.searchUsers(query)
        .then((d) => { if (mySeq === seqRef.current) { setResults(d?.items || []); setActive(0); } })
        .catch(() => { if (mySeq === seqRef.current) setResults([]); });
    }, 120);
    return () => clearTimeout(t);
  }, [open, query]);

  // Restore the caret after a token insertion re-renders the textarea.
  useEffect(() => {
    if (caretRef.current == null) return;
    const ta = taRef.current;
    const pos = caretRef.current;
    caretRef.current = null;
    if (ta) {
      ta.focus();
      try { ta.setSelectionRange(pos, pos); } catch { /* noop */ }
    }
  }, [value]);

  function pick(user) {
    const ta = taRef.current;
    if (!ta || !user) return;
    const caret = ta.selectionStart;
    const token = buildMentionToken(user.name || user.email, user.id);
    // Function replacer so `$`-sequences in a name can't be interpreted by replace().
    const before = value.slice(0, caret).replace(/@([\w.-]*)$/, () => `${token} `);
    const after = value.slice(caret);
    caretRef.current = before.length; // place caret just after the inserted token + space
    onChange(before + after);
    setOpen(false);
    setResults([]);
    setQuery('');
  }

  function onKeyDown(e) {
    // Let IME composition commit — the Enter that confirms a CJK/IME candidate
    // fires with isComposing (keyCode 229) and must never send.
    if (e.isComposing || e.keyCode === 229) return;
    if (open && results.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => (a + 1) % results.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (a - 1 + results.length) % results.length); return; }
      if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') { e.preventDefault(); pick(results[active]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setOpen(false); return; }
      // Shift+Enter falls through → default newline; the value change closes the menu.
    }
    // Dropdown closed → Enter sends, Shift+Enter inserts a newline.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit?.();
    }
  }

  return (
    <div className="mention-input" style={{ flex: 1, minWidth: 0 }}>
      <textarea
        ref={taRef}
        data-testid="messenger-composer-input"
        value={value}
        onChange={update}
        onKeyDown={onKeyDown}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        rows={rows}
        placeholder={placeholder || 'Write a message… (Enter to send, @ to mention)'}
        disabled={disabled}
        aria-label="Message"
      />
      {open && results.length > 0 && (
        <ul className="mention-dropdown" data-testid="messenger-mention-menu" role="listbox">
          {results.map((u, i) => (
            <li
              key={u.id}
              role="option"
              aria-selected={i === active}
              className={i === active ? 'active' : ''}
              data-testid="messenger-mention-option"
              onMouseDown={(e) => { e.preventDefault(); pick(u); }}
            >
              <span className="avatar tiny">{(u.name || u.email || '?').charAt(0).toUpperCase()}</span>
              <div>
                <div>{u.name}</div>
                <div className="muted small">{u.email}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
